<!-- 実際にエントリーする機能だけではなく、エントリーに関係する機能全てを包含したボタンとして扱う -->
<template>
  <div 
    class="button_container" 
    :class="{ 'fade-out': isScrolledToBottom }"
  >
    <!-- エントリー/プレエントリー/スコア加算切り替えができる場合 -->
    <template v-if="canEntryEvent">
      <!-- プレエントリー -->
      <button v-if="isPreEntryButton" class="button__entry" @click="$emit('clickPreEntry')">
        {{ $t('preEntryEvent') }}
      </button>
      <!-- スコア加算切り替え -->
      <button v-else-if="isSwitchingScoringEventButton" class="button__scoring_switch" @click="$emit('clickSwitchingScoringEvent')">
        {{ $t('switchToScoringEvent') }}
      </button>
      <!-- エントリー -->
      <button v-else class="button__entry" @click="$emit('clickEntry')">
        {{ $t('entryEvent') }}
      </button>
    </template>

    <button v-else-if="isCancelPreEntryButton" class="button__cancel_pre_entry" @click="$emit('clickCancelPreEntry')">
      {{ $t('cancelEventPreEntry') }}
    </button>
    
    <!-- エントリーできない for ランクイベント -->
    <template v-else-if="isUserRankEvent">
      <button v-if="!isRankEnabled" class="button__entry" @click="$emit('clickOptin')">
        {{ $t('user_rank_event_entry_button__show_rank_optin_screen') }}
      </button>
      <button v-else class="button__disable" disabled>
        {{ eventNotEnterableMessage }}
      </button>
    </template>

    <!-- エントリーできない for その他のイベント -->
    <button v-else class="button__disable" disabled>
      {{ $t('cannotEntryEvent') }}
    </button>
  </div>
</template>

<script lang="ts">
import { TranslateResult } from 'vue-i18n'
import { Vue, Component, Prop } from 'vue-property-decorator'
import { BroadcasterEvent } from '@/pages/events/models/BroadcasterEvent'
import { BroadcasterEventEntry } from '@/pages/events/models/BroadcasterEventEntry'
import dayjs from 'dayjs'

@Component({
  name: 'EventEntryButton',
})
export default class EventEntryButton extends Vue {
  @Prop({ type: BroadcasterEvent })
  event?: BroadcasterEvent
  @Prop({ type: Boolean })
  isRankEnabled?: boolean // ランクが有効化されているかどうか
  @Prop({ type: Boolean })
  isRankConditionSatisfied?: boolean // ランクの条件が満たされているかどうか
  @Prop({ type: BroadcasterEventEntry })
  entry?: BroadcasterEventEntry // イベントに対応するエントリー情報

  private isScrolledToBottom = false
  private scrollThreshold = 100 // 最下部から何pxでフェードアウトするか
  private scrollTimeout: number | null = null
  private isTransitioning = false

  created(): void {
  }

  mounted(): void {
    this.setupScrollListener()
  }

  beforeDestroy(): void {
    this.removeScrollListener()
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
  }

  private setupScrollListener(): void {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  }

  private removeScrollListener(): void {
    window.removeEventListener('scroll', this.handleScroll)
  }

  private handleScroll(): void {
    // 既にトランジション中なら処理をスキップ
    if (this.isTransitioning) {
      return
    }

    const scrollTop = window.scrollY
    const windowHeight = window.visualViewport?.height ?? window.innerHeight
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    )
    
    // 最下部から一定距離以内にスクロールしたかチェック
    const isNearBottom = scrollTop + windowHeight >= documentHeight - this.scrollThreshold
    
    if (isNearBottom !== this.isScrolledToBottom) {
      // デバウンス処理：短時間での連続変更を防ぐ
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }
      
      this.scrollTimeout = window.setTimeout(() => {
        this.isTransitioning = true
        this.isScrolledToBottom = isNearBottom
        
        // トランジション完了後にフラグをリセット
        setTimeout(() => {
          this.isTransitioning = false
        }, 300) // CSSトランジション時間と合わせる
      }, 50) // 50msのデバウンス
    }
  }

  get eventNotEnterableMessage(): TranslateResult {
    if (!this.isRankConditionSatisfied) {
      return this.$t('user_rank_event_entry_button__rank_condition_is_not_satisfied')
    }
    return this.$t('user_rank_event_entry_button__other_condition_is_not_satisfied')
  }

  get canEntryEvent(): boolean {
    return !!this.event?.canEntry
  }

  get isUserRankEvent(): boolean {
    return !!this.event?.isUserRankEvent()
  }

  get isPreEntryButton(): boolean {
    return this.canEntryEvent && !this.event?.isDoing(dayjs()) && this.entry === undefined
  }

  get isCancelPreEntryButton(): boolean {
    return !this.canEntryEvent && !this.event?.beginAt.isBefore(dayjs()) && this.entry !== undefined
  }

  get isSwitchingScoringEventButton(): boolean {
    return this.canEntryEvent && this.entry !== undefined
  }
}
</script>

<style scoped>
.button__entry {
  min-height: 50px;
  padding-left: 15px;
  padding-right: 15px;
  min-width: 180px;
  max-width: 410px;
  background-color: rgb(233, 34, 82);
  border-radius: 25px;
  border-width: 0px;
  color: white;
  font-size: 16px;
}

.button__disable {
  min-height: 50px;
  padding-left: 15px;
  padding-right: 15px;
  min-width: 180px;
  max-width: 410px;
  background-color: #e8e8e8;
  border-radius: 25px;
  border-width: 0px;
  color: #808080;
  font-size: 16px;
}

.button__scoring_switch {
  min-height: 50px;
  padding-left: 15px;
  padding-right: 15px;
  min-width: 180px;
  max-width: 410px;
  background-color: #188dff;
  border-radius: 25px;
  border-width: 0px;
  color: white;
  font-size: 16px;
}

.button__cancel_pre_entry {
  min-height: 50px;
  padding-left: 15px;
  padding-right: 15px;
  min-width: 180px;
  max-width: 410px;
  background-color: rgb(233, 34, 82);
  border-radius: 25px;
  border-width: 0px;
  color: white;
  font-size: 16px;
}

.button_container {
  text-align: center;
  transition: opacity 0.3s ease-in-out;
  opacity: 1;
  pointer-events: auto;
}

.button_container.fade-out {
  opacity: 0;
  pointer-events: none;
}

/* フェードアウト時は子要素も含めて全てのタッチイベントを無効化 */
.button_container.fade-out * {
  pointer-events: none !important;
}
</style>
